import axios from 'axios';
import api from '../api/api';
import config from '../config/environment';

class KYCValidationService {
  constructor() {
    this.api = axios.create({
      baseURL: `${config.merchantServiceUrl}/api`,
      timeout: 60000,
      withCredentials: true,
    });
  }

  // Document Authenticity Validation
  async validateDocumentAuthenticity(file, documentType) {
    try {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('documentType', documentType);

      const response = await this.api.post('/kyc/validate-document-authenticity', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      return {
        isAuthentic: response.data.isAuthentic,
        confidence: response.data.confidence,
        tamperingDetected: response.data.tamperingDetected,
        qualityScore: response.data.qualityScore,
        extractedData: response.data.extractedData,
        securityFeatures: response.data.securityFeatures
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Document Expiry Validation
  async validateDocumentExpiry(documentData, documentType) {
    try {
      const response = await this.api.post('/kyc/validate-document-expiry', {
        documentData,
        documentType
      });

      return {
        isValid: response.data.isValid,
        expiryDate: response.data.expiryDate,
        daysUntilExpiry: response.data.daysUntilExpiry,
        isExpired: response.data.isExpired,
        warningRequired: response.data.warningRequired
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Face Match Validation
  async validateFaceMatch(identityDocument, addressDocument, selfieImage = null) {
    try {
      const formData = new FormData();
      formData.append('identityDocument', identityDocument);
      formData.append('addressDocument', addressDocument);
      if (selfieImage) {
        formData.append('selfieImage', selfieImage);
      }

      const response = await this.api.post('/kyc/validate-face-match', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      return {
        matchScore: response.data.matchScore,
        isMatch: response.data.isMatch,
        confidence: response.data.confidence,
        faceDetected: response.data.faceDetected,
        qualityChecks: response.data.qualityChecks,
        livenessScore: response.data.livenessScore
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Data Consistency Validation
  async validateDataConsistency(extractedData, formData) {
    try {
      const response = await this.api.post('/kyc/validate-data-consistency', {
        extractedData,
        formData
      });

      return {
        isConsistent: response.data.isConsistent,
        inconsistencies: response.data.inconsistencies,
        matchingFields: response.data.matchingFields,
        confidenceScore: response.data.confidenceScore,
        recommendations: response.data.recommendations
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Comprehensive KYC Validation
  async performComprehensiveValidation(merchantId, documents, formData) {
    try {
      const validationResults = {
        merchantId,
        timestamp: new Date().toISOString(),
        overallStatus: 'PENDING',
        validationSteps: {},
        finalScore: 0,
        recommendations: []
      };

      // Step 1: Document Authenticity Check
      console.log('🔍 Step 1: Validating document authenticity...');
      for (const doc of documents) {
        const authenticityResult = await this.validateDocumentAuthenticity(doc.file, doc.type);
        validationResults.validationSteps[`authenticity_${doc.type}`] = {
          status: authenticityResult.isAuthentic ? 'PASSED' : 'FAILED',
          confidence: authenticityResult.confidence,
          details: authenticityResult
        };
      }

      // Step 2: Document Expiry Check
      console.log('📅 Step 2: Validating document expiry...');
      const identityDoc = documents.find(d => d.type === 'IDENTITY');
      if (identityDoc && identityDoc.extractedData) {
        const expiryResult = await this.validateDocumentExpiry(identityDoc.extractedData, identityDoc.type);
        validationResults.validationSteps.expiry_check = {
          status: expiryResult.isValid ? 'PASSED' : 'FAILED',
          details: expiryResult
        };
      }

      // Step 3: Face Match Validation
      console.log('👤 Step 3: Performing face match validation...');
      const identityDocument = documents.find(d => d.type === 'IDENTITY')?.file;
      const addressDocument = documents.find(d => d.type === 'ADDRESS')?.file;
      const selfieImage = documents.find(d => d.type === 'SELFIE')?.file;

      if (identityDocument && (addressDocument || selfieImage)) {
        const faceMatchResult = await this.validateFaceMatch(identityDocument, addressDocument, selfieImage);
        validationResults.validationSteps.face_match = {
          status: faceMatchResult.isMatch ? 'PASSED' : 'FAILED',
          confidence: faceMatchResult.confidence,
          details: faceMatchResult
        };
      }

      // Step 4: Data Consistency Check
      console.log('📊 Step 4: Validating data consistency...');
      const extractedData = this.consolidateExtractedData(documents);
      const consistencyResult = await this.validateDataConsistency(extractedData, formData);
      validationResults.validationSteps.data_consistency = {
        status: consistencyResult.isConsistent ? 'PASSED' : 'FAILED',
        confidence: consistencyResult.confidenceScore,
        details: consistencyResult
      };

      // Step 5: Calculate Final Score and Status
      validationResults.finalScore = this.calculateFinalScore(validationResults.validationSteps);
      validationResults.overallStatus = this.determineOverallStatus(validationResults.finalScore, validationResults.validationSteps);
      validationResults.recommendations = this.generateRecommendations(validationResults.validationSteps);

      // Step 6: Update KYC Status
      await this.updateKYCStatus(merchantId, validationResults);

      return validationResults;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Status Transition Management
  async updateKYCStatus(merchantId, validationResults) {
    try {
      const statusTransition = {
        merchantId,
        previousStatus: 'PENDING_VALIDATION',
        newStatus: validationResults.overallStatus,
        validationScore: validationResults.finalScore,
        validationDetails: validationResults.validationSteps,
        timestamp: new Date().toISOString(),
        autoApproved: validationResults.finalScore >= 75  // Lowered from 85 to 75
      };

      const response = await this.api.post('/kyc/update-status-transition', statusTransition);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Test Identity Document Validation
  async validateTestIdentityDocument(file) {
    // Simulate comprehensive identity document validation
    return new Promise((resolve) => {
      setTimeout(() => {
        const mockResult = {
          isAuthentic: true,
          confidence: 0.95,
          tamperingDetected: false,
          qualityScore: 0.92,
          extractedData: {
            name: 'John Doe',
            dateOfBirth: '1990-01-15',
            documentNumber: 'ABCDE1234F',
            issueDate: '2020-01-01',
            expiryDate: '2030-01-01',
            address: '123 Test Street, Test City, 123456'
          },
          securityFeatures: {
            hologram: true,
            microtext: true,
            uvInk: true,
            rfidChip: true
          }
        };
        resolve(mockResult);
      }, 2000);
    });
  }

  // Test Address Document Validation
  async validateTestAddressDocument(file) {
    // Simulate comprehensive address document validation
    return new Promise((resolve) => {
      setTimeout(() => {
        const mockResult = {
          isAuthentic: true,
          confidence: 0.88,
          tamperingDetected: false,
          qualityScore: 0.85,
          extractedData: {
            name: 'John Doe',
            address: '123 Test Street, Test City, 123456',
            documentType: 'UTILITY_BILL',
            issueDate: '2024-11-01',
            serviceProvider: 'Test Electricity Board'
          },
          addressVerification: {
            pincode: '123456',
            city: 'Test City',
            state: 'Test State',
            addressMatch: true
          }
        };
        resolve(mockResult);
      }, 1500);
    });
  }

  // Utility Methods
  consolidateExtractedData(documents) {
    const consolidated = {};
    documents.forEach(doc => {
      if (doc.extractedData) {
        consolidated[doc.type] = doc.extractedData;
      }
    });
    return consolidated;
  }

  calculateFinalScore(validationSteps) {
    const weights = {
      authenticity: 0.3,
      expiry_check: 0.2,
      face_match: 0.25,
      data_consistency: 0.25
    };

    let totalScore = 0;
    let totalWeight = 0;

    Object.keys(validationSteps).forEach(step => {
      const stepData = validationSteps[step];
      const weight = weights[step.split('_')[0]] || 0.1;
      
      if (stepData.status === 'PASSED') {
        totalScore += (stepData.confidence || 1) * weight * 100;
      }
      totalWeight += weight;
    });

    return totalWeight > 0 ? Math.round(totalScore / totalWeight) : 0;
  }

  determineOverallStatus(finalScore, validationSteps) {
    const criticalFailures = Object.values(validationSteps).filter(
      step => step.status === 'FAILED' && (step.confidence || 0) < 0.5
    );

    if (criticalFailures.length > 0) {
      return 'REJECTED';
    }

    if (finalScore >= 75) {  // Lowered from 85 to 75
      return 'APPROVED';
    } else if (finalScore >= 60) {  // Lowered from 70 to 60
      return 'MANUAL_REVIEW';
    } else {
      return 'REJECTED';
    }
  }

  generateRecommendations(validationSteps) {
    const recommendations = [];

    Object.entries(validationSteps).forEach(([step, data]) => {
      if (data.status === 'FAILED') {
        switch (step) {
          case 'authenticity_IDENTITY':
            recommendations.push('Identity document appears to be tampered or low quality. Please upload a clear, original document.');
            break;
          case 'authenticity_ADDRESS':
            recommendations.push('Address document quality is insufficient. Please upload a recent, clear document.');
            break;
          case 'expiry_check':
            recommendations.push('Document is expired or expiring soon. Please upload a valid document.');
            break;
          case 'face_match':
            recommendations.push('Face match confidence is low. Please ensure clear photos and proper lighting.');
            break;
          case 'data_consistency':
            recommendations.push('Data inconsistencies found between documents and form. Please verify all information.');
            break;
        }
      }
    });

    return recommendations;
  }

  handleError(error) {
    if (error.response) {
      return {
        message: error.response.data?.error || 'Validation service error',
        status: error.response.status,
        data: error.response.data
      };
    } else if (error.request) {
      return {
        message: 'Network error - validation service unavailable',
        status: 0,
        data: null
      };
    } else {
      return {
        message: error.message || 'Validation error occurred',
        status: 0,
        data: null
      };
    }
  }
}

export default new KYCValidationService();